import { useEffect, useState } from 'react';
import { BedDouble, Brain, Flame, Pause, Phone, Play, Wind } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/* Interactive box-breathing coach (4s in, 4s hold, 4s out, 4s hold)  */
/* ------------------------------------------------------------------ */

const BREATH_PHASES = [
  { label: 'Breathe in', scale: 1.35 },
  { label: 'Hold', scale: 1.35 },
  { label: 'Breathe out', scale: 1 },
  { label: 'Hold', scale: 1 },
] as const;

const CYCLE_SECONDS = 16; // 4 phases × 4 seconds

function BreathingCoach() {
  const [tick, setTick] = useState(0);
  const [running, setRunning] = useState(true);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const cycleTick = tick % CYCLE_SECONDS;
  const phaseIndex = Math.floor(cycleTick / 4);
  const secondsLeft = 4 - (cycleTick % 4);
  const phase = BREATH_PHASES[phaseIndex];

  return (
    <div className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h4 className="flex items-center gap-2 text-base font-bold text-rose-700">
          <Wind className="size-5" aria-hidden />
          1. Slow box breathing
        </h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setRunning((r) => !r)}
          className="h-9 rounded-xl font-semibold text-rose-700 hover:bg-rose-50"
          aria-label={running ? 'Pause breathing guide' : 'Resume breathing guide'}
        >
          {running ? <Pause className="size-4" aria-hidden /> : <Play className="size-4" aria-hidden />}
          {running ? 'Pause' : 'Resume'}
        </Button>
      </div>

      <p className="text-sm leading-relaxed text-rose-950/70">
        Follow the circle — in for 4, hold for 4, out for 4, hold for 4. Slow breathing cuts the
        panic circuit and stops muscles from tensing up around the pain.
      </p>

      <div className="flex flex-col items-center gap-3 py-2">
        <div
          aria-hidden
          className="flex size-32 items-center justify-center rounded-full bg-gradient-to-br from-rose-300 to-rose-500 shadow-lg transition-transform ease-in-out"
          style={{
            transform: `scale(${phase.scale})`,
            transitionDuration: running ? '4000ms' : '300ms',
          }}
        >
          <span className="text-3xl font-black tabular-nums text-white">{secondsLeft}</span>
        </div>
        <p className="text-lg font-bold text-rose-800" aria-live="polite">
          {phase.label}…
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SOS guide                                                          */
/* ------------------------------------------------------------------ */

const STEPS = [
  {
    icon: Flame,
    title: '2. Warmth on the cramp',
    body: 'Apply a heated compress, hot water bottle, or warm towel to your lower pelvis or back. Heat opens local blood vessels and eases heavy muscle spasms. (No heat source? A warm shower works too.)',
  },
  {
    icon: BedDouble,
    title: '3. Restorative position',
    body: 'Lie on your left side in a loose fetal tuck with a pillow between your knees. This unloads the spine and releases tension across the pelvis and lower back.',
  },
  {
    icon: Brain,
    title: '4. Calm self-talk',
    body: 'Remind yourself: “This flare is intense, but it is a false alarm. I am safe. My body will settle soon.” Reassurance relaxes the anxious bracing that amplifies pain.',
  },
] as const;

export function SosGuide() {
  return (
    <div className="space-y-5 rounded-3xl border border-rose-200 bg-rose-50 p-5 text-rose-950">
      <h3 className="border-b border-rose-200 pb-3 text-xl font-black text-rose-900">
        Acute flare-up SOS protocol
      </h3>

      <div className="space-y-4">
        <BreathingCoach />

        {STEPS.map(({ icon: Icon, title, body }) => (
          <div key={title} className="space-y-2 rounded-2xl bg-white p-5 shadow-sm">
            <h4 className="flex items-center gap-2 text-base font-bold text-rose-700">
              <Icon className="size-5 shrink-0" aria-hidden />
              {title}
            </h4>
            <p className="text-sm leading-relaxed text-rose-950/70">{body}</p>
          </div>
        ))}
      </div>

      {/* Emergency services (works on phones) */}
      <div className="space-y-3 rounded-2xl border border-rose-300 bg-white p-5">
        <p className="text-sm font-semibold leading-relaxed text-rose-900">
          Fainting, fever, one-sided pain, or pain that feels different from your usual flare can be
          an emergency — don&apos;t wait it out.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Button asChild className="h-13 rounded-2xl bg-rose-600 text-base font-black text-white hover:bg-rose-600/90">
            <a href="tel:999" aria-label="Call emergency services on 999">
              <Phone className="size-5" aria-hidden />
              Call 999
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="h-13 rounded-2xl border-rose-300 text-base font-black text-rose-700 hover:bg-rose-100"
          >
            <a href="tel:112" aria-label="Call emergency services on 112">
              <Phone className="size-5" aria-hidden />
              Call 112
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
